import { IStorageAdapter } from '../types';
import { StandardStorageKeys } from '../storage/standard-keys';
import { StorageLock } from '../storage-lock';
import { PKCEManager } from './pkce-manager';

/**
 * Creates a PKCE Manager instance with standard storage keys.
 *
 * If a `namespace` is provided, the code verifier storage keys are suffixed
 * with the namespace so that multiple independent PKCE managers (for example,
 * one per extension `appId`) never share storage slots.
 *
 * NOTE: The storage adapter interprets `string[]` keys as a *fallback-read
 * list* (try each in order, write to `key[0]`), not as a composite namespace.
 * Namespaced managers therefore produce a single-element composite key such
 * as `['CODE_VERIFIER.<namespace>']` so they land in a unique storage slot
 * that never collides with the default instance or with other namespaced
 * managers.
 *
 * @param storageAdapter Storage adapter for persisting code verifiers
 * @param memoryOnly If true, operates in memory-only mode (no locks)
 * @param namespace Optional namespace to isolate this PKCE manager's storage
 *   from the default one (e.g. an extension appId)
 * @returns PKCE Manager instance
 */
export function createPKCEManager(
  storageAdapter: IStorageAdapter,
  memoryOnly = false,
  namespace?: string
): PKCEManager {
  const baseCodeVerifierKey = StandardStorageKeys['CODE_VERIFIER'];
  const baseNextCodeVerifierKey = StandardStorageKeys['NEXT_CODE_VERIFIER'];

  const codeVerifierKey = namespace
    ? [`${baseCodeVerifierKey[0]}.${namespace}`]
    : baseCodeVerifierKey;
  const nextCodeVerifierKey = namespace
    ? [`${baseNextCodeVerifierKey[0]}.${namespace}`]
    : baseNextCodeVerifierKey;

  // Namespaced instances also get a namespaced StorageLock. Without this,
  // every PKCE manager in the app (host + N extensions) would serialize on
  // the same Web-Lock / localStorage slot `tc_lock_pkce_rotation`, so a
  // PKCE rotation on one extension would block rotations on every other
  // extension and the host — the opposite of what we want.
  const lock = memoryOnly
    ? undefined
    : namespace
      ? new StorageLock({ keyPrefix: `tc_lock_${namespace}_` })
      : undefined;

  return new PKCEManager(
    storageAdapter,
    codeVerifierKey,
    nextCodeVerifierKey,
    lock,
    memoryOnly
  );
}
